import type { ApiQuestion } from '../question/models/ApiQuestion'
import type { Price, Question, QuestionType } from '../question/models/Question'

const BUY_SCORE_THRESHOLD = 4

export const isBuyScore = (score: number): boolean => score >= BUY_SCORE_THRESHOLD

export const chooseStartIndex = ({
  pricePointIds,
  startPointId,
}: {
  pricePointIds: readonly string[]
  startPointId: string | undefined
}): number => {
  const configuredIndex = startPointId === undefined ? -1 : pricePointIds.indexOf(startPointId)
  if (configuredIndex !== -1) return configuredIndex
  return Math.floor(Math.random() * pricePointIds.length)
}

export type GaborGrangerStaircase = {
  currentIndex: () => number
  isComplete: () => boolean
  recordAnswer: (score: number) => void
}

// A respondent only ever sees prices that sit next to each other in the list, which is what
// lets their answers be saved as an ordinary grid answer.
export const createGaborGrangerStaircase = ({
  pricePointCount,
  startIndex,
}: {
  pricePointCount: number
  startIndex: number
}): GaborGrangerStaircase => {
  let currentIndex = startIndex
  let stepsUp: boolean | undefined
  let complete = false

  return {
    currentIndex: () => currentIndex,
    isComplete: () => complete,
    recordAnswer: (score: number): void => {
      const answeredYes = isBuyScore(score)
      stepsUp ??= answeredYes

      const nextIndex = currentIndex + (stepsUp ? 1 : -1)
      if (answeredYes !== stepsUp || nextIndex < 0 || nextIndex >= pricePointCount) {
        complete = true
        return
      }
      currentIndex = nextIndex
    },
  }
}

// The cards are already in price order, so take the ids from them rather than the payload.
// Both an interview and a preview hold their prices this way, a preview question being a
// question without its id, so both build their staircase from the same two functions.
export const gaborGrangerPriceIds = (
  questions: readonly { type: QuestionType; price?: Price }[],
): string[] =>
  questions.flatMap(({ type, price }) =>
    type === 'gaborGranger' && price !== undefined ? [price.id] : [],
  )

export const createStaircaseForPriceIds = ({
  pricePointIds,
  startPointId,
}: {
  pricePointIds: readonly string[]
  startPointId: string | undefined
}): GaborGrangerStaircase =>
  createGaborGrangerStaircase({
    pricePointCount: pricePointIds.length,
    startIndex: chooseStartIndex({ pricePointIds, startPointId }),
  })

export const createStaircaseForApiQuestion = ({
  apiQuestion,
  questions,
}: {
  apiQuestion: ApiQuestion
  questions: readonly Question[]
}): GaborGrangerStaircase | undefined => {
  if (apiQuestion.type !== 'gabor_granger') return undefined

  return createStaircaseForPriceIds({
    pricePointIds: gaborGrangerPriceIds(questions),
    startPointId: apiQuestion['start-point'],
  })
}
